import React, { useEffect } from 'react';
import { Card, Col, Row, Select, Skeleton, Space, Tooltip } from 'antd'
import StoreItemCard from '../components/Store/StoreItemCard';
import { ColSize } from 'antd/lib/col';
import { StoreItem } from '../services/api/GraphqlTypes';
import useUser from '../services/hooks/useUser';
import ShoppingCart from '../components/Store/ShoppingCart/ShoppingCart';
import ContentPageLayout from '../components/Layouts/ContentPageLayout';
import { SortAscendingOutlined, SortDescendingOutlined } from '@ant-design/icons';
import FlexSpace from '../components/Helpers/FlexSpace';
import { useMemo } from 'react';
import UserWidget from '../components/Widgets/UserWidget/UserWidget';
import AppStore from 'services/stores/AppStore';
import { runInAction, toJS } from 'mobx';
import { Observer } from 'mobx-react-lite';
import classNames from 'classnames';

const skeletonItem: StoreItem = {
    id: 0,
    name: "Item",
    description: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Curabitur in blandit magna.",
    descriptionHtml: "<b>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Curabitur in blandit magna.</b>",
    image: "",
    price: 10,
    compareAtPrice: 0,
    categoryIds: [],
    useStock: false,
    stock: 0
}

const skeletonCards = 9;
const loadingItems: StoreItem[] = [];
for (let i = 0; i < skeletonCards; i++) {
    loadingItems.push({ ...skeletonItem, id: Math.random() });
}

const Store: React.FC = () => {
    const [user] = useUser();
    const store = useMemo(() => AppStore.createStoreItemsStore(), []);

    useEffect(() => {
        return () => store.dispose();
    }, [store]);

    const handleCategoryChange = (categoryId: number) => {
        runInAction(() => {
            if (store.activeCategories?.contains(categoryId)) {
                store.activeCategories.remove(categoryId);
            } else {
                store.activeCategories = [categoryId];
            }
        });
    }

    return (
        <ContentPageLayout
            pageHeader="Store"
        >
            <Row wrap gutter={[16, 16]} className="store">
                <Col
                    span={24}
                    lg={18}
                >
                    <Row style={{ marginBottom: 16 }}>
                        <FlexSpace />

                        <span className="sort-by-label">Sort By:</span>
                        <Observer>{() => (
                            <Select value={store.sortBy} onChange={x => runInAction(() => store.sortBy = x)} style={{ width: 120 }}>
                                <Select.Option value="category">Category</Select.Option>
                                <Select.Option value="price-asc">Price <SortAscendingOutlined /></Select.Option>
                                <Select.Option value="price-desc">Price <SortDescendingOutlined /></Select.Option>
                            </Select>
                        )}</Observer>
                    </Row>

                    <Row align="bottom" style={{ marginBottom: 16 }}>
                        <Observer>{() => (
                            <Space direction="horizontal">
                                {store.loadingCategories ? (new Array(5).map((x, i) => (
                                    <Skeleton.Button
                                        active
                                        key={`sk-${i}`}
                                    />
                                ))) : (
                                    store.categories.map(cat => {
                                        const active = store.activeCategories.length === 1 && store.activeCategories[0] === cat.id;
                                        return (
                                            <Tooltip title={cat.name} key={`cat-${cat.id}`}>
                                                <Card
                                                    hoverable
                                                    className={classNames("category-card", { "active": !!active })}
                                                    cover={<img alt={cat.name} src={cat.image || "data:image/png;base64,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"} />}
                                                    onClick={() => handleCategoryChange(cat.id)}
                                                >
                                                    {/* <Meta title={cat.name} /> */}
                                                </Card>
                                            </Tooltip>
                                        );
                                    })
                                )}
                            </Space>
                        )}</Observer>
                    </Row>

                    <Row wrap gutter={[16, 16]}>
                        <Observer>{() => {
                            toJS(store.loadingItems && store.loadingCategories);
                            const loading = store.loadingItems || store.loadingCategories;
                            const items = toJS(store.storeItemsFilteredAndSorted);
                            return (
                                <>
                                    {items.map((item, i) => {
                                        return (
                                            <Col
                                                lg={getLgBreakpointProps(i, items.length)}
                                                sm={getMdBreakpointProps(i, items.length)}
                                                span={24}
                                                key={item.id}
                                            >
                                                <StoreItemCard
                                                    skeleton={loading}
                                                    storeItem={item}
                                                />
                                            </Col>
                                        );
                                    })}
                                </>
                            )
                        }}</Observer>
                    </Row>
                </Col>

                <Col span={24} lg={{ span: 6, order: 2 }}>
                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <UserWidget />
                        </Col>

                        {user && (
                            <Col span={24}>
                                <ShoppingCart />
                            </Col>
                        )}
                    </Row>
                </Col>
            </Row>
        </ContentPageLayout>
    );
}

const getLgBreakpointProps = (index: number, size: number): ColSize => {
    const isLast = index === size - 1;
    const isSecondToLast = index === size - 2
    const remaining1 = size % 3 === 1;
    const remaining2 = size % 3 === 2;

    let longerWidth = false;
    let offsetCenter = false;

    if (isLast) {
        if (remaining1 || remaining2) {
            longerWidth = true;
        }
        if (remaining1) {
            offsetCenter = true;
        }
    }

    if (isSecondToLast && remaining2) {
        longerWidth = true;
    }
    return {
        span: longerWidth ? 12 : 8,
        offset: offsetCenter ? 6 : 0
    };
}

const getMdBreakpointProps = (index: number, size: number): ColSize => {
    const isLast = index === size - 1;
    const remaining1 = size % 2 === 1;
    const longerWidth = isLast && remaining1;

    return {
        span: longerWidth ? 16 : 12,
        offset: longerWidth ? 4 : 0
    };
}

export default Store;
